import moment from 'moment';
import * as Yup from 'yup';
import helpers from 'src/helpers';
import { isEmpty, isPhone } from 'src/helpers/check';
import { phoneToString, toArray, stringToNull } from 'src/helpers/transform';
import { COUNTRIES } from 'src/constants';
// import { yupToFormErrors } from 'formik';

export const getValidatePhone = (value) => isEmpty(value) || isPhone(phoneToString(value));

// CON-2010 - an existing phone the user did not change never blocks a save
// (e.g. a 5-6 digit short code an inbound text saved as phone1). New or edited
// values keep the getValidatePhone rule. Digits are compared because the phone
// mask reformats the value once the user types in the field.
export const getValidatePhoneUnlessUnchanged = (initialValue) => (value) => (
  getValidatePhone(value)
  || (!isEmpty(initialValue) && phoneToString(value) === phoneToString(initialValue))
);

export const postUSARegExp = /^(0050[1-9])|(005[1-9][0-9])|(00[6-9][0-9][0-9])|(0[1-9][0-9][0-9][0-9])|(999[0-4][0-9])|(99950)|(99[0-8][0-9][0-9])|(9[0-8][0-9][0-9][0-9])|([1-8][0-9][0-9][0-9][0-9])$/;

export const postCanadaRegExp = /^(?!.*[DFIOQU])[A-VXY][0-9][A-Z][ -]?[0-9][A-Z][0-9]$/;

export const onlyLettersNumbersSpacesDashesUnderscoresRegExp = /^[0-9a-z _-]*$/i;

export const postValidation = (countryFieldName, valueName) => (Yup.string()
  .when(countryFieldName, {
    is: (country) => country === 'United States' || country?.value === COUNTRIES.UNITED_STATES,
    then: Yup.string()
      .matches(postUSARegExp, `${valueName} is not valid`)
      .min(5, `${valueName} is not valid`)
      .max(5, `${valueName} is not valid`),
  })
  .when(countryFieldName, {
    is: (country) => country === 'Canada' || country?.value === COUNTRIES.CANADA,
    then: Yup.string()
      .matches(postCanadaRegExp, `${valueName} is not valid`)
      .min(3, `${valueName} is not valid`)
      .max(7, `${valueName} is not valid`),
  })
);

export const dateType1Validation = () => (Yup.date('Invalid date')
  .nullable(true)
  .typeError('Invalid date')
  .min(new Date(1900, 1, 1), 'Invalid date')
  .max(new Date(3000, 12, 31), 'Invalid date')
);

export const campaignTextFieldValidation = (valueName) => (Yup.string()
  .matches(onlyLettersNumbersSpacesDashesUnderscoresRegExp, `${valueName} may only contain letters, numbers, spaces, dashes, and underscores`)
  .min(1, 'At least one character')
  .max(200, `${valueName} must be at most 200 characters`)
);

export const campaignTextFieldDropDownValidation = (valueName, allowableTags) => (
  Yup.object().shape({
    value: Yup.string()
      .matches(onlyLettersNumbersSpacesDashesUnderscoresRegExp, `${valueName} may only contain letters, numbers, spaces, dashes, and underscores`)
      .min(1, 'At least one character')
      .max(200, `${valueName} must be at most 200 characters`)
      .oneOf(allowableTags, `${valueName} contains an invalid value`)
  })
    .typeError(`${valueName} may only contain letters, numbers, spaces, dashes, and underscores`)
    .nullable(true)
);

export const mayOnlyLettersNumbersSpacesDashesUnderscores = (values) => (
  isEmpty(values) || !toArray(values).find((value) => !(value?.value || value).match(onlyLettersNumbersSpacesDashesUnderscoresRegExp))
);

export const campaignArrayFieldWithTextValidation = (valueName) => (Yup.array()
  .test('additionalTagsArrValidation', `${valueName} may only contain letters, numbers, spaces, dashes, and underscores`, mayOnlyLettersNumbersSpacesDashesUnderscores)
);

export const validateField = (event, setFieldTouched) => {
  const { target, dataName } = event || {};
  const { name } = target || {};
  if (!isEmpty(name) || !isEmpty(dataName)) {
    setFieldTouched(stringToNull(name) || stringToNull(dataName), true);
  }
};

export const cutMaxStrLen = (str, length) => {
  const newStr = str.slice(0, length);
  // The length of emoji is one or two chars. There is last char 55357 in string if we cut emoji.
  return newStr.charCodeAt(newStr.length - 1) === 55357 ? cutMaxStrLen(newStr, length - 1) : newStr;
};

export const validateDateFromMoment = (year, month, day) => {
  if (year && month && day) {
    return moment([parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10)]).invalidAt();
  }
  return -1;
};

export const validateCurrencyAmount = () => (
  Yup.string().matches(/^\d{0,6}(\.[0-9]{2})?$/, 'Not a valid currency amount')
);

export const validatePhone1OrEmail = (values) => {
  const {
    phone1,
    email,
  } = values || {};

  return (phone1 || email);
};

export const validationUrlWithoutHttp = async (value) => {
  if (helpers.isEmpty(value)) {
    return true; // Allow empty values
  }

  const url = `https://${helpers.removeHttpFromUrl(value)}`;
  const urlValidation = Yup.object().shape({
    url: Yup.string().url()
  });

  const validationResult = await Yup.reach(urlValidation, 'url').validate(url)
    .then(() => true)
    .catch(() => false);
  return validationResult;
};

export const customFieldsValidation = (customFields = []) => Yup.array().of(
  Yup.object().shape({
    name: Yup.string()
      .max(30, 'Custom Field name must be at most 30 characters.')
      .required('Custom Field Name is required.')
      .test('unique-name', 'This name already exists', (value) => {
        if (!value) return true;

        const normalized = value.toLowerCase().trim();
        return customFields.filter((field) => field?.name?.toLowerCase().trim() === normalized).length <= 1;
      }),
    value: Yup.string(),
  })
);
